import React, { useContext, useState, useEffect } from "react";
import { Button } from "primereact/button";
import { Accordion, AccordionTab } from "primereact/accordion";
import * as Yup from "yup";
import Input from "../../../../components/form/input";
import InputArea from "../../../../components/form/inputArea";
import DatePicker from "../../../../components/form/DatePicker";
import { formatErrors, handleSubmit } from "../../../../services/utils/processors";
import { ToastContext } from "../../../../services/contexts/toast-context";
import { FormWrapper, FormSection, GridRow, AccordionHeader, AddButtonRow, ActionRow } from "../form.styles";

function formatDateFields(items) {
  return items.map(item => ({
    ...item,
    start_date: item.start_date ? (String(item.start_date).length === 4 ? new Date(`${item.start_date}-01-01`) : new Date(item.start_date)) : null,
    end_date: item.end_date ? (String(item.end_date).length === 4 ? new Date(`${item.end_date}-12-31`) : new Date(item.end_date)) : null,
  }));
}

const Projects = ({ dbData = null, loading = false, onSave, onBack }) => {
  const toastRef = useContext(ToastContext);
  const [projects, setProjects] = useState([]);
  const [initialProjects, setInitialProjects] = useState([]);
  const [projectError, setProjectError] = useState({});
  const baselineKey = JSON.stringify(dbData || []);

  const schema = Yup.array().of(
    Yup.object({
      title: Yup.string().required("Project title is required"),
      description: Yup.string().required("Description is required"),
      start_date: Yup.date().nullable(),
      end_date: Yup.date().nullable(),
    })
  );

  useEffect(() => {
    const baseline = Array.isArray(dbData) ? formatDateFields(dbData) : [];
    setProjects(baseline);
    setInitialProjects(JSON.parse(JSON.stringify(baseline)));
    setProjectError({});
  }, [baselineKey]);

  const normalize = (items) => items.map((item) => ({
    title: item.title?.trim() || '',
    description: item.description?.trim() || '',
    start_date: item.start_date instanceof Date ? item.start_date.toISOString().slice(0, 10) : item.start_date || '',
    end_date: item.end_date instanceof Date ? item.end_date.toISOString().slice(0, 10) : item.end_date || '',
  })).sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b)));
  const isDirty = JSON.stringify(normalize(projects)) !== JSON.stringify(normalize(initialProjects));

  const handleInputChange = (value, index, field) => {
    const updated = [...projects];
    updated[index] = { ...updated[index], [field]: value };
    setProjects(updated);
  };

  const addProject = () => {
    setProjects([...projects, { title: "", description: "", start_date: null, end_date: null }]);
  };

  const removeProject = (index) => {
    setProjects(projects.filter((_, i) => i !== index));
  };

  const handleSubmitData = () => {
    handleSubmit(projects, schema, setProjectError, onSave);
    if (projectError && Object.entries(projectError).length !== 0) {
      toastRef.current.show({ severity: "error", summary: "Error", detail: formatErrors(projectError), life: 6000 });
    }
    setProjectError({});
  };

  return (
    <FormWrapper>
      <AddButtonRow>
        <Button type="button" label="Add Project" icon="pi pi-plus" className="p-button-success" onClick={addProject} />
      </AddButtonRow>

      <Accordion style={{ width: "100%" }}>
        {projects.map((project, index) => (
          <AccordionTab key={index}
            header={
              <AccordionHeader>
                <span>{project.title || `Project ${index + 1}`}</span>
                <Button type="button" icon="pi pi-trash" className="p-button-rounded p-button-danger p-button-text"
                  onClick={(event) => { event.stopPropagation(); removeProject(index); }} tooltip="Remove" tooltipOptions={{ position: "top" }} />
              </AccordionHeader>
            }
            style={{ minWidth: "50vw", backgroundColor: "transparent", border: "none", marginBottom: 16 }}
          >
            <FormSection>
              <Input value={project.title} label="Title" width="100%"
                onChange={(v) => handleInputChange(v, index, "title")} error={projectError?.[index]?.title} />
              <GridRow>
                <DatePicker value={project.start_date} label="Start Date" width="100%"
                  onChange={(v) => handleInputChange(v, index, "start_date")} error={projectError?.[index]?.start_date} />
                <DatePicker value={project.end_date} label="End Date" width="100%"
                  onChange={(v) => handleInputChange(v, index, "end_date")} error={projectError?.[index]?.end_date} />
              </GridRow>
              <InputArea value={project.description} label="Description" width="100%" height="120px"
                onChange={(v) => handleInputChange(v, index, "description")} error={projectError?.[index]?.description} />
            </FormSection>
          </AccordionTab>
        ))}
      </Accordion>

      <ActionRow>
        {onBack && <Button label="Back" icon="pi pi-arrow-left" severity="secondary" onClick={onBack} />}
        {isDirty && <Button label="Save" icon="pi pi-save" loading={loading} className="p-button-success" onClick={handleSubmitData} />}
      </ActionRow>
    </FormWrapper>
  );
};

export default Projects;
